<template>
  <b-container class="mt-5">
    <h4 class="ml-3 mt-2">Files</h4>
    <div class="d-flex justify-content-end mb-2">
      <b-button
        variant="outline-primary"
        @click="showUploadModal"
        class="float-left mr-2"
      >
        Upload XLS <b-icon icon="upload" />
      </b-button>
    </div>
    <b-table
      thead-class="text-center bg-light-grey"
      head-variant="light"
      bordered
      small
      show-empty
      outlined
      stacked="md"
      :fields="fields"
      :items="files.data"
      id="internalCoincidencesTable"
      :busy="isLoadingFiles"
      class="mt-2 compact-table table"
    >
      <template #empty>
        <div class="text-center">
          <h6>{{ $t("thereAreNoRecordsToShow") }}</h6>
        </div>
      </template>
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner variant="primary" class="align-middle"></b-spinner>
          <p>Loading</p>
        </div>
      </template>
      <template v-slot:cell(createdAt)="{ item }">
        {{ item.createdAt | formatDate }}
      </template>
      <template v-slot:cell(actions)="{ item }">
        <!-- create a link to open item link -->

        <b-icon
          icon="eye"
          class="icon-color cursor-pointer mr-2"
          @click="openUrl(item.link)"
        />
        <i>
          <font-awesome-icon
            :icon="['far', 'cars']"
            @click="getVehiclesUploaded(item.id)"
          />
        </i>
      </template>
    </b-table>
    <!-- Upload Modal -->
    <b-modal
      id="upload-xls-modal"
      title="Upload XLS File"
      hide-footer
      centered
      size="lg"
      @close="resetUploadForm"
    >
      <b-form @submit.prevent="uploadFile">
        <b-form-group label="Select Provider:">
          <vue-multiselect
            v-model="selectedProvider"
            :options="providers"
            :searchable="true"
            :close-on-select="true"
            :show-labels="false"
            placeholder="Select a provider"
            track-by="id"
            label="name"
          />
        </b-form-group>

        <b-form-group label="Select XLS File:">
          <b-form-file
            v-model="xlsFile"
            :state="Boolean(xlsFile)"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            accept=".xls,.xlsx"
          ></b-form-file>
          <b-form-text> Only XLS/XLSX files are accepted </b-form-text>
        </b-form-group>
        <!-- button to see reference -->
        <b-button
          variant="link"
          class="text-primary"
          @click="openUrl(REFERENCE_URL)"
        >
          See Reference
        </b-button>
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="$bvModal.hide('upload-xls-modal')"
          >
            Cancel
          </b-button>
          <b-button
            variant="success"
            type="submit"
            :disabled="!isFormValid || isUploading"
          >
            Upload
            <b-spinner
              v-if="isUploading"
              small
              label="Uploading..."
            ></b-spinner>
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </b-container>
</template>
<script>
import { GET_FILES } from "./actions";
import { mapState } from "vuex";
export default {
  name: "FilesTable",
  computed: {
    ...mapState({
      files: (state) => state.adminStore.files.files.data || {},
      isLoadingFiles: (state) => state.adminStore.files.status === "fetching",
      providersListData: (state) =>
        state.adminStore.vehiclesProviderPrice.providersList.data,
      isProvidersLoading: (state) =>
        state.adminStore.vehiclesProviderPrice.providersList.status ===
        "fetching",
    }),
    isFormValid() {
      return this.selectedProvider && this.xlsFile;
    },
    providers() {
      return this.providersListData || [];
    },
  },
  data() {
    return {
      fields: [
        { key: "name", label: "File Name" },
        { key: "supplier.name", label: "Supplier" },

        { key: "createdAt", label: "Date" },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          class: "text-center",
        },
      ],
      selectedProvider: null,
      xlsFile: null,
      isUploading: false,
      REFERENCE_URL:
        "https://proleasing.nyc3.cdn.digitaloceanspaces.com/Prod/system/Excel.png", // Replace with actual URL
    };
  },
  methods: {
    getData() {
      this.$store
        .dispatch(GET_FILES)
        .then((response) => {
          console.log("Files fetched successfully:", response);
        })
        .catch((error) => {
          console.error("Error fetching files:", error);
        });
    },

    getVehiclesUploaded(id) {
      this.$router.push({
        name: "VehiclesUploaded",
        params: { id: id },
      });
    },
    formatFileName(type) {
      const date = new Date();
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${type}-${year}-${month}-${day}-${hours}${minutes}`;
    },
    showUploadModal() {
      // Load providers list if not already loaded
      if (!this.providers.length && !this.isProvidersLoading) {
        this.loadProviders();
      }
      this.$bvModal.show("upload-xls-modal");
    },

    loadProviders() {
      this.$store
        .dispatch("GET_SUPPLIERS_LIST")
        .then(() => {})
        .catch((error) => {
          console.error("Error loading providers:", error);
          this.$bvToast.toast("Failed to load providers", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        });
    },

    uploadFile() {
      if (!this.selectedProvider || !this.xlsFile) {
        this.$bvToast.toast("Please select provider and file", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.isUploading = true;

      const formData = new FormData();
      formData.append("id", this.selectedProvider.id);
      formData.append("file", this.xlsFile);

      // Simulate upload for now - replace with actual API call
      this.$store
        .dispatch("UPLOAD_VEHICLE_VALUATION_XLS", formData)
        .then((response) => {
          console.log("File uploaded successfully:", response);
          this.$root.$bvToast.toast("File uploaded successfully", {
            title: "Success",
            variant: "success",
            solid: true,
          });
          this.resetUploadForm();
          this.getData(); // Refresh the file list
        })
        .catch((error) => {
          console.error("Error uploading file:", error);
          this.$root.$bvToast.toast("Failed to upload file", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.isUploading = false;
        });
    },

    resetUploadForm() {
      this.selectedProvider = null;
      this.xlsFile = null;
      this.isUploading = false;
    },

    openUrl(url) {
      window.open(url, "_blank");
    },
  },
  mounted() {
    this.getData();
  },
};
</script>
